import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { SHIPPING_AUTOMATION } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Autocorrector.styled';

const childStyle = { width: '5rem' };

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const Autocorrector = () => {
	// States and Hooks
	const axios = useAxiosPrivate();

	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();
	const { generalSettings, isSupportUser } = useSelector((state) => state.user);

	const {
		dataCorrectorAddressFromLatLon,
		dataCorrectorCoordinate,
		dataCorrectorArea,
		dataCorrectorCalculateDistance,
		dataCorrectorOnHoldProduct,
		dataCorrectorBlacklist,
		dataCorrectorNeedConfirm,
		dataCorrectorCODConfirmation,
		orderConfirmationDelayTime,
		synchronizedCorrection,
		dataCorrectorHSCorrector,
		dataCorrectorIgnoreMissingCity
	} = generalSettings;

	const fields = useMemo(
		() => [
			'dataCorrectorArea',
			'dataCorrectorCalculateDistance',
			'dataCorrectorOnHoldProduct',
			'dataCorrectorBlacklist',
			'dataCorrectorNeedConfirm',
			'dataCorrectorCODConfirmation',
			'dataCorrectorHSCorrector',
			'dataCorrectorIgnoreMissingCity'
		],
		[]
	);

	// Form Schema
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					dataCorrectorAddressFromLatLon: yup.boolean(),
					dataCorrectorCoordinate: yup.boolean(),
					dataCorrectorArea: yup.boolean(),
					dataCorrectorCalculateDistance: yup.boolean(),
					dataCorrectorOnHoldProduct: yup.boolean(),
					dataCorrectorBlacklist: yup.boolean(),
					dataCorrectorNeedConfirm: yup.boolean(),
					dataCorrectorCODConfirmation: yup.boolean(),
					synchronizedCorrection: yup.boolean(),
					dataCorrectorHSCorrector: yup.boolean(),
					dataCorrectorIgnoreMissingCity: yup.boolean(),
					orderConfirmationDelayTime: yup
						.number()
						.nullable()
						.when('dataCorrectorCODConfirmation', {
							is: true,
							then: (schema) => schema.required('orderdelaytimenotnull')
						})
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			dataCorrectorAddressFromLatLon,
			dataCorrectorCoordinate,
			dataCorrectorArea,
			dataCorrectorCalculateDistance,
			dataCorrectorOnHoldProduct,
			dataCorrectorBlacklist,
			dataCorrectorNeedConfirm,
			dataCorrectorCODConfirmation,
			orderConfirmationDelayTime,
			synchronizedCorrection,
			dataCorrectorHSCorrector,
			dataCorrectorIgnoreMissingCity
		}
	});

	// Form Methods
	const {
		watch,
		handleSubmit,
		reset,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const dataCorrectorCODConfirmationWatch = watch('dataCorrectorCODConfirmation');
	const onSubmit = async (data) => {
		try {
			const response = await axios.post(SHIPPING_AUTOMATION.UPDATE_AUTOMATION_SETTINGS, {
				dataCorrector: {
					...data,
					orderConfirmationDelayTime: data.dataCorrectorCODConfirmation
						? data.orderConfirmationDelayTime
						: null
				}
			});

			if (response.status === 200) {
				showSuccess();
				dispatch(setUserDetails({ generalSettings: { ...generalSettings, ...data } }));
				reset(data);
			} else {
				showError({
					header: 'noChangesError',
					message: 'noChangeErrorMessage'
				});
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'noChangesError',
				message: 'noChangeErrorMessage'
			});
		}
	};

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="autocorrectorSettings" />
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						className="saveButton"
						onClick={handleSubmit(onSubmit, AppLogger.log)}
						icon={faFloppyDisk}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label="save"
						variant="blue"
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>
			<S.StyledForm formMethods={formMethods} borderless>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					<FormItem
						isLoading={isLoading}
						direction="row-reverse"
						description="dataCorrectorAddressFromLatLon.description"
						label="dataCorrectorAddressFromLatLon"
						childStyle={childStyle}>
						<SwitchInput
							value={watch('dataCorrectorAddressFromLatLon')}
							setValue={(e) => {
								setValue('dataCorrectorAddressFromLatLon', e.target.checked, validateOptions);
								setValue('dataCorrectorCoordinate', false, validateOptions);
							}}
						/>
					</FormItem>
					<FormItem
						isLoading={isLoading}
						direction="row-reverse"
						label="dataCorrectorCoordinate"
						description="dataCorrectorCoordinate.description"
						childStyle={childStyle}>
						<SwitchInput
							value={watch('dataCorrectorCoordinate')}
							setValue={(e) => {
								setValue('dataCorrectorCoordinate', e.target.checked, validateOptions);
								setValue('dataCorrectorAddressFromLatLon', false, validateOptions);
							}}
						/>
					</FormItem>
					{fields.map((field) => (
						<FormItem
							key={field}
							isLoading={isLoading}
							direction="row-reverse"
							description={`${field}.description`}
							label={field}
							childStyle={childStyle}>
							<SwitchInput name={field} />
						</FormItem>
					))}

					{isSupportUser && (
						<FormItem
							isLoading={isLoading}
							label="synchronizedCorrection"
							description="synchronizedCorrection.description"
							direction="row-reverse"
							childStyle={childStyle}>
							<SwitchInput name="synchronizedCorrection" />
						</FormItem>
					)}
					{dataCorrectorCODConfirmationWatch && (
						<FormItem
							label="setOrderConfirmationTime"
							isLoading={isLoading}
							required={dataCorrectorCODConfirmationWatch}
							toolTip="orderConfirmationTimeTooltipTitle">
							<IntegerInput name="orderConfirmationDelayTime" showArrows={false} />
						</FormItem>
					)}
				</S.StyledCard>
			</S.StyledForm>
			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					className="saveButton"
					onClick={handleSubmit(onSubmit, AppLogger.log)}
					icon={faFloppyDisk}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="save"
					variant="blue"
				/>
			</FormFooter>
		</div>
	);
};

export default Autocorrector;
